/**
 * LGPD COMPLIANCE - PRIVACY CONSENT COMPONENT
 * ============================================
 * Implementa conformidade com LGPD (Lei Geral de Proteção de Dados)
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Shield, Eye, FileText, Users, Lock, Info, CheckCircle, AlertTriangle } from 'lucide-react';

interface ConsentData {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  timestamp: string;
  version: string;
}

interface PrivacyConsentProps {
  onConsentUpdate: (consent: ConsentData) => void;
}

export const PrivacyConsent: React.FC<PrivacyConsentProps> = ({ onConsentUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentData>({
    essential: true, // Sempre true - cookies técnicos obrigatórios
    analytics: false,
    marketing: false,
    personalization: false,
    timestamp: new Date().toISOString(),
    version: '1.0',
  });

  // Verificar se já existe consentimento
  useEffect(() => {
    const existingConsent = localStorage.getItem('lgpd_consent');
    if (!existingConsent) {
      setShowModal(true);
    } else {
      try {
        const parsed = JSON.parse(existingConsent);
        setConsent(parsed);
        onConsentUpdate(parsed);
      } catch (e) {
        setShowModal(true);
      }
    }
  }, [onConsentUpdate]);

  // Adicionar/remover classe no body quando banner estiver visível
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('lgpd-banner-visible');
    } else {
      document.body.classList.remove('lgpd-banner-visible');
    }

    // Cleanup ao desmontar
    return () => {
      document.body.classList.remove('lgpd-banner-visible');
    };
  }, [showModal]);

  const handleConsentSave = () => {
    const finalConsent = {
      ...consent,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem('lgpd_consent', JSON.stringify(finalConsent));
    localStorage.setItem('lgpd_consent_date', new Date().toISOString());

    onConsentUpdate(finalConsent);
    setShowModal(false);
  };

  const handleConsentChange = (category: keyof ConsentData, value: boolean) => {
    if (category === 'essential') return; // Não permitir desabilitar cookies essenciais

    setConsent(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const consentCategories = [
    {
      id: 'essential' as keyof ConsentData,
      title: 'Cookies Essenciais',
      description: 'Necessários para funcionamento básico do site (autenticação, segurança)',
      required: true,
      icon: Lock,
      examples: ['Sessão de usuário', 'Configurações de segurança', 'Carrinho de compras'],
    },
    {
      id: 'analytics' as keyof ConsentData,
      title: 'Análise e Performance',
      description: 'Ajudam a entender como você usa o site para melhorarmos a experiência',
      required: false,
      icon: Eye,
      examples: ['Google Analytics', 'Métricas de performance', 'Mapas de calor'],
    },
    {
      id: 'marketing' as keyof ConsentData,
      title: 'Marketing e Publicidade',
      description: 'Permitem mostrar anúncios relevantes e medir eficácia de campanhas',
      required: false,
      icon: Users,
      examples: ['Meta Pixel', 'Google Ads', 'Remarketing'],
    },
    {
      id: 'personalization' as keyof ConsentData,
      title: 'Personalização',
      description: 'Personalizam conteúdo e recomendações baseadas no seu comportamento',
      required: false,
      icon: FileText,
      examples: ['Recomendações de imóveis', 'Preferências de busca', 'Histórico de navegação'],
    },
  ];

  return (
    <>
      {/* Banner de Consentimento */}
      {showModal && (
        <div
          className='fixed bottom-0 left-0 right-0 max-w-[90%] mx-auto mb-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30'
          style={{
            zIndex: 9999,
            paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          <div className='max-w-7xl mx-auto p-4 sm:p-6'>
            <div className='flex items-start justify-between space-x-4'>
              <div className='flex items-start space-x-3'>
                <Shield className='h-5 w-5 text-blue-600 mt-1 flex-shrink-0' />
                <div className='flex-1'>
                  <h3 className='text-base sm:text-lg font-semibold text-gray-900 dark:text-white'>
                    Política de Privacidade - LGPD
                  </h3>
                  <p className='text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-2'>
                    Usamos cookies e tecnologias similares para melhorar sua experiência. Você pode
                    escolher quais tipos de cookies aceitar.
                  </p>
                  <div className='flex flex-col sm:flex-row gap-2 mt-3'>
                    <Button
                      variant='outline'
                      size='sm'
                      className='py-1.5 px-3 text-sm'
                      onClick={() => setShowDetails(true)}
                    >
                      <Info className='h-3 w-3 mr-2' />
                      Ver Detalhes
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='py-1.5 px-3 text-sm'
                      onClick={() => {
                        setConsent(prev => ({
                          ...prev,
                          analytics: false,
                          marketing: false,
                          personalization: false,
                        }));
                        handleConsentSave();
                      }}
                    >
                      Apenas Essenciais
                    </Button>
                    <Button
                      size='sm'
                      className='bg-blue-600 hover:bg-blue-700 py-1.5 px-3 text-sm'
                      onClick={() => {
                        setConsent(prev => ({
                          ...prev,
                          analytics: true,
                          marketing: true,
                          personalization: true,
                        }));
                        handleConsentSave();
                      }}
                    >
                      Aceitar Todos
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center'>
              <Shield className='h-5 w-5 mr-2 text-blue-600' />
              Configurações de Privacidade - LGPD
            </DialogTitle>
            <DialogDescription>
              Conforme a Lei Geral de Proteção de Dados (LGPD), você tem controle sobre como seus
              dados são coletados e usados.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-6'>
            {/* Resumo dos Direitos */}
            <Card className='border-blue-200 bg-blue-50 dark:bg-blue-900/20'>
              <CardHeader>
                <CardTitle className='text-lg'>Seus Direitos LGPD</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Acesso aos seus dados</span>
                    </div>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Correção de dados incorretos</span>
                    </div>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Exclusão de dados</span>
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Portabilidade dos dados</span>
                    </div>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Revogação do consentimento</span>
                    </div>
                    <div className='flex items-center'>
                      <CheckCircle className='h-4 w-4 text-green-600 mr-2' />
                      <span className='text-sm'>Informações sobre uso</span>
                    </div>
                  </div>
                </div>
                <p className='text-sm text-gray-600 dark:text-white mt-4'>
                  Para exercer seus direitos, entre em contato:{' '}
                  <strong>dpo@chatbotimoveis.com.br</strong>
                </p>
              </CardContent>
            </Card>

            {/* Categorias de Cookies */}
            <div className='space-y-4'>
              <h3 className='text-xl font-semibold'>Categorias de Dados e Cookies</h3>

              {consentCategories.map(category => {
                const IconComponent = category.icon;
                const isEnabled = Boolean(consent[category.id]);

                return (
                  <Card
                    key={category.id}
                    className={`${
                      isEnabled
                        ? 'border-green-200 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200'
                    }`}
                  >
                    <CardHeader>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center space-x-3'>
                          <IconComponent
                            className={`h-5 w-5 ${isEnabled ? 'text-green-600' : 'text-gray-500'}`}
                          />
                          <div>
                            <CardTitle className='text-lg'>{category.title}</CardTitle>
                            <CardDescription>{category.description}</CardDescription>
                          </div>
                        </div>
                        <div className='flex items-center space-x-2'>
                          {category.required && <Badge variant='secondary'>Obrigatório</Badge>}
                          <Checkbox
                            checked={isEnabled}
                            disabled={category.required}
                            onCheckedChange={checked =>
                              handleConsentChange(category.id, checked as boolean)
                            }
                          />
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <div className='space-y-2'>
                        <p className='text-sm font-medium'>Exemplos de uso:</p>
                        <ul className='text-sm text-gray-600 dark:text-white space-y-1'>
                          {category.examples.map((example, index) => (
                            <li key={index} className='flex items-center'>
                              <span className='w-1.5 h-1.5 bg-gray-400 rounded-full mr-2' />
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Ações */}
            <div className='flex items-center justify-between pt-6 border-t'>
              <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
                <AlertTriangle className='h-4 w-4 mr-2' />
                Você pode alterar essas preferências a qualquer momento
              </div>
              <div className='flex space-x-3'>
                <Button variant='outline' onClick={() => setShowDetails(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleConsentSave} className='bg-blue-600 hover:bg-blue-700'>
                  Salvar Preferências
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
